import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { ActionListResponse } from "../actions.types";

/**
 * Arguments for useFetchActions hook.
 */
export type UseFetchActionsArgs = {
  /** Program ID to fetch actions for (optional - if not provided, fetches all user actions) */
  programId?: number;
  /** Current user (to determine if logged in) */
  user: { id: number } | null;
  /** Whether the query is enabled */
  isEnabled?: boolean;
};

/**
 * Hook to fetch actions.
 * If programId is provided, fetches actions for that program via /actions/:programId/
 * If programId is not provided, fetches all user actions via /actions/
 *
 * @example
 * ```typescript
 * // Fetch actions for a specific program
 * const { data, isLoading } = useFetchActions({
 *   programId: 123,
 *   user,
 * });
 *
 * // Fetch all user actions
 * const { data, isLoading } = useFetchActions({ user });
 *
 * const actions = data?.actions ?? [];
 * ```
 */
export function useFetchActions({
  programId,
  user,
  isEnabled = true,
}: UseFetchActionsArgs) {
  const endpoint = programId ? `/actions/${programId}/` : "/actions/";

  return useQuery<ActionListResponse, Error>({
    queryKey: ["actions", programId ?? "all"],
    queryFn: async () => {
      return apiRequest(endpoint, "GET") as Promise<ActionListResponse>;
    },
    enabled: !!user && isEnabled,
    placeholderData: keepPreviousData,
  });
}
